import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FolderLock, FolderOpen, Lock } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatDate, useEnumLabel, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, InfoHint, InlineAlert, Input, NativeSelect, Skeleton, SourceText, Textarea, useSave, useServerForm } from "@ncp/ui";

type Access = "Full" | "Summary" | "Withheld";
interface IndexEntry { documentId: string; title: string; category: string; classification: string; filedAt: string; filedBy: string; access: Access; summary?: string | null; summaryAr?: string | null; reason?: string | null; reasonAr?: string | null }
interface FileAccessView {
  required: boolean; open: boolean; inspections: number; canManage: boolean;
  window?: { id: string; status: "Open" | "Closed"; openedAt: string; closesAt: string; closedAt?: string | null; openedByName: string; closedByName?: string | null; closedReason?: string | null } | null;
  index: IndexEntry[]; parties: { id: string; name: string; nameAr?: string | null; role: string; linked: boolean }[];
  /** The period the file is opened for: its default and bounds, in working days, from their one definition (condition 2.14). */
  defaultDays: number; minDays: number; maxDays: number; levels: Access[]; reasons: { key: string; label: string; labelAr: string }[];
}
interface Choice { access: Access; summary: string; summaryAr: string; reason: string }

const OTHER = "__other";
const TONE: Record<Access, "success" | "warning" | "outline"> = { Full: "success", Summary: "warning", Withheld: "outline" };

/**
 * Access to the file before an adverse decision: the index of the file with what each undertaking concerned may see, the period it is
 * open for on the portal, and every inspection recorded. The report does not go forward until the period has run or every undertaking has answered.
 */
export function FileAccessCard({ caseId }: { caseId: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const enumLabel = useEnumLabel();
  const api = useApi();
  const qc = useQueryClient();
  const view = useQuery({ queryKey: ["file-access", caseId], queryFn: async () => (await api.get<FileAccessView | undefined>(`/api/cases/${caseId}/file-access`)) ?? null });
  const [mode, setMode] = useState<"idle" | "open" | "close">("idle");
  // The index refetched brings the recorded access, summaries and reasons only into those the officer has not changed since (S 2.7).
  const [choices, setChoices] = useServerForm<Record<string, Choice>>(view.data ? Object.fromEntries(view.data.index.map((i) => [i.documentId, { access: i.access, summary: i.summary ?? "", summaryAr: i.summaryAr ?? "", reason: i.reason ?? "" }])) : null, {}, caseId);
  const [days, setDays] = useServerForm<number | null>(view.data?.defaultDays ?? null, null, caseId);
  const [closeReason, setCloseReason] = useState("");

  const refresh = () => { for (const key of ["file-access", "case", "case-tasks", "case-letters", "case-workflow"]) void qc.invalidateQueries({ queryKey: key === "file-access" || key === "case" ? [key, caseId] : [key] }); };
  const open = useSave({
    mutationFn: () => api.post(`/api/cases/${caseId}/file-access`, {
      days,
      items: Object.entries(choices).map(([documentId, c]) => ({ documentId, access: c.access, summary: c.summary.trim() || null, summaryAr: c.summaryAr.trim() || null, reason: c.reason.trim() || null })),
    }),
    saved: t("backoffice:fileAccess.openedToast"),
    onSuccess: () => { setMode("idle"); refresh(); },
  });
  const close = useSave({
    mutationFn: () => api.post(`/api/cases/${caseId}/file-access/close`, { reason: closeReason.trim() }),
    saved: t("backoffice:fileAccess.closedToast"),
    onSuccess: () => { setMode("idle"); setCloseReason(""); refresh(); },
  });

  if (view.isLoading) return <Skeleton className="h-40" />;
  const v = view.data;
  if (!v) return null;
  const w = v.window;
  const ar = locale === "ar";
  const label = "text-[11px] font-semibold uppercase tracking-wider text-sand-600";
  const reasonText = (reason?: string | null, reasonAr?: string | null) => (ar && reasonAr ? reasonAr : reason ? <SourceText text={reason} /> : null);
  const standard = (reason: string) => v.reasons.some((r) => r.label === reason);
  const set = (id: string, patch: Partial<Choice>) => setChoices((c) => ({ ...c, [id]: { ...c[id]!, ...patch } }));
  const incomplete = Object.values(choices).some((c) => (c.access === "Summary" && c.summary.trim().length < 20) || (c.access === "Withheld" && c.reason.trim().length < 10));
  const badge = v.open && w ? <Badge variant="success" size="sm">{t("backoffice:fileAccess.openUntil", { date: formatDate(w.closesAt, locale) })}</Badge>
    : w ? <Badge variant="outline" size="sm">{t("backoffice:fileAccess.closed")}</Badge>
    : v.required ? <Badge variant="warning" size="sm">{t("backoffice:fileAccess.notOpened")}</Badge> : null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base"><FolderOpen className="size-4 text-aegold-700" /> {t("backoffice:fileAccess.title")}<InfoHint>{t("backoffice:fileAccess.hint")}</InfoHint><span className="ms-auto">{badge}</span></CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!v.required && !w ? <p className="text-xs text-muted-foreground">{t("backoffice:fileAccess.notRequired")}</p> : null}
        {v.parties.length === 0 ? <InlineAlert tone="info" title={t("backoffice:fileAccess.noPartiesTitle")}>{t("backoffice:fileAccess.noParties")}</InlineAlert> : (
          <div>
            <p className={`mb-1 ${label}`}>{t("backoffice:fileAccess.parties")}</p>
            <div className="flex flex-wrap gap-2">
              {v.parties.map((p) => (
                <span key={p.id} className="inline-flex items-center gap-1.5 rounded-lg border border-sand-200 px-2 py-1 text-xs">
                  {ar && p.nameAr ? p.nameAr : <SourceText text={p.name} />}
                  <Badge size="sm" variant={p.linked ? "info" : "outline"}>{p.linked ? t("backoffice:fileAccess.portal") : t("backoffice:fileAccess.letterOnly")}</Badge>
                </span>
              ))}
            </div>
          </div>
        )}
        {w ? (
          <div className="rounded-xl bg-sand-50 p-3 text-xs">
            <p>{t("backoffice:fileAccess.openedBy", { name: w.openedByName, date: formatDate(w.openedAt, locale) })} · {t("backoffice:fileAccess.closes", { date: formatDate(w.closesAt, locale) })}</p>
            {w.status === "Closed" && w.closedAt ? <p className="mt-1">{t("backoffice:fileAccess.closedBy", { name: w.closedByName ?? "—", date: formatDate(w.closedAt, locale) })}{w.closedReason ? <> – <SourceText text={w.closedReason} /></> : null}</p> : null}
            <p className="mt-1 text-muted-foreground">{t("backoffice:fileAccess.inspections", { count: v.inspections })}</p>
          </div>
        ) : null}

        <div>
          <p className={`mb-1 ${label}`}>{t("backoffice:fileAccess.index")} <span className="normal-case tracking-normal text-muted-foreground">({v.index.length})</span></p>
          {v.index.length === 0 ? <p className="text-xs text-muted-foreground">{t("backoffice:fileAccess.empty")}</p> : (
            <ul className="divide-y divide-sand-100 rounded-xl border border-sand-200">
              {v.index.map((i) => {
                const c = choices[i.documentId];
                const editing = mode === "open" && c;
                return (
                  <li key={i.documentId} className="space-y-2 px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <SourceText text={i.title} className="min-w-0 flex-1 truncate font-medium" />
                      <Badge size="sm" variant="outline">{enumLabel("classification", i.classification)}</Badge>
                      <span className="text-[11px] text-muted-foreground">{formatDate(i.filedAt, locale)}</span>
                      {editing ? (
                        <NativeSelect aria-label={t("backoffice:fileAccess.access")} className="h-8 w-44" value={c.access} onChange={(e) => set(i.documentId, { access: e.target.value as Access, reason: e.target.value === i.access ? i.reason ?? "" : "" })}>
                          {v.levels.map((l) => <option key={l} value={l}>{t(`backoffice:fileAccess.levels.${l}`)}</option>)}
                        </NativeSelect>
                      ) : <Badge size="sm" variant={TONE[i.access]}>{i.access === "Withheld" ? <Lock /> : null} {t(`backoffice:fileAccess.levels.${i.access}`)}</Badge>}
                    </div>
                    {!editing && i.reason ? <p className="text-[11px] text-muted-foreground">{reasonText(i.reason, i.reasonAr)}</p> : null}
                    {!editing && i.summary ? <p className="rounded-lg bg-sand-50 px-2 py-1 text-xs">{ar && i.summaryAr ? i.summaryAr : <SourceText text={i.summary} />}</p> : null}
                    {editing && c.access === "Summary" ? (
                      <div className="grid gap-2 md:grid-cols-2">
                        <Field label={t("backoffice:fileAccess.summary")} hint={t("backoffice:fileAccess.summaryHint")}>{(id) => <Textarea id={id} rows={2} dir="ltr" lang="en" value={c.summary} onChange={(e) => set(i.documentId, { summary: e.target.value })} draft={`case:${caseId}:file-access:${i.documentId}:summary`} />}</Field>
                        <Field label={t("backoffice:fileAccess.summaryAr")}>{(id) => <Textarea id={id} rows={2} dir="rtl" lang="ar" value={c.summaryAr} onChange={(e) => set(i.documentId, { summaryAr: e.target.value })} draft={`case:${caseId}:file-access:${i.documentId}:summaryAr`} />}</Field>
                      </div>
                    ) : null}
                    {editing && c.access === "Withheld" ? (
                      <div className="grid gap-2 md:grid-cols-2">
                        <Field label={t("backoffice:fileAccess.reason")}>{(id) => (
                          <NativeSelect id={id} value={standard(c.reason) ? c.reason : OTHER} onChange={(e) => set(i.documentId, { reason: e.target.value === OTHER ? "" : e.target.value })}>
                            {v.reasons.filter((r) => r.key !== "own" && r.key !== "public").map((r) => <option key={r.key} value={r.label}>{ar ? r.labelAr : r.label}</option>)}
                            <option value={OTHER}>{t("backoffice:fileAccess.otherReason")}</option>
                          </NativeSelect>
                        )}</Field>
                        {!standard(c.reason) ? <Field label={t("backoffice:fileAccess.ownReason")}>{(id) => <Input id={id} dir="auto" value={c.reason} onChange={(e) => set(i.documentId, { reason: e.target.value })} />}</Field> : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {mode === "open" ? (
          <div className="flex flex-wrap items-end gap-2 rounded-xl border border-sand-200 p-3">
            <Field label={t("backoffice:fileAccess.days")} hint={t("backoffice:fileAccess.daysHint", { min: v.minDays, max: v.maxDays })}>{(id) => <Input id={id} type="number" min={v.minDays} max={v.maxDays} dir="ltr" className="h-8 w-28" value={days ?? v.defaultDays} onChange={(e) => setDays(Number(e.target.value))} />}</Field>
            <Button variant="gold" size="sm" loading={open.isPending} disabled={incomplete || (days ?? v.defaultDays) < v.minDays || (days ?? v.defaultDays) > v.maxDays} onClick={() => open.mutate()}><FolderOpen /> {t("backoffice:fileAccess.open")}</Button>
            <Button variant="ghost" size="sm" onClick={() => setMode("idle")}>{t("backoffice:fileAccess.cancel")}</Button>
            {incomplete ? <p className="basis-full text-[11px] text-warning-700">{t("backoffice:fileAccess.incomplete")}</p> : null}
          </div>
        ) : null}
        {mode === "close" ? (
          <div className="space-y-2 rounded-xl border border-sand-200 p-3">
            <Field label={t("backoffice:fileAccess.closeReason")} hint={t("backoffice:fileAccess.closeReasonHint")}>{(id) => <Textarea id={id} rows={2} dir="auto" value={closeReason} onChange={(e) => setCloseReason(e.target.value)} draft={`case:${caseId}:file-access:close:reason`} />}</Field>
            <div className="flex flex-wrap gap-2">
              <Button variant="gold" size="sm" loading={close.isPending} disabled={closeReason.trim().length < 20} onClick={() => close.mutate()}><FolderLock /> {t("backoffice:fileAccess.close")}</Button>
              <Button variant="ghost" size="sm" onClick={() => setMode("idle")}>{t("backoffice:fileAccess.cancel")}</Button>
            </div>
          </div>
        ) : null}

        {mode === "idle" && v.parties.length > 0 ? (
          v.canManage ? (
            <div className="flex flex-wrap gap-2">
              {!v.open ? <Button variant={w ? "outline" : "gold"} size="sm" onClick={() => setMode("open")}><FolderOpen /> {w ? t("backoffice:fileAccess.reopen") : t("backoffice:fileAccess.prepare")}</Button> : null}
              {v.open ? <Button variant="outline" size="sm" onClick={() => setMode("close")}><FolderLock /> {t("backoffice:fileAccess.closeEarly")}</Button> : null}
            </div>
          ) : <p className="text-xs text-muted-foreground">{t("backoffice:fileAccess.role")}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
